import { useEffect, useState, useMemo } from 'react'
import { startsWith } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Box, Flex, SystemProps, css } from '@storyofams/react-ui'
import styled from 'styled-components'
import { Button, Text, QuantityInputVariants, Select } from '~components'
import { useShopify } from '~context'
import { Product, ProductVariant, CartLine } from '~lib/shopify/sdk'
import { Image } from '../Image'
import { formatPrice, gaRemoveFromCartEvent } from '~lib'
import { shopifyDiscountPriceFactor } from '~config'

export const ImageWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  &:after {
    content: '';
    width: 100%;
    height: 100%;
  }
`

const messages = defineMessages({
  add: 'Add',
  addedToBox: 'is added to your box',
  viewBox: 'View box',
  remove: 'Remove',
})

interface VariantCardProps extends SystemProps {
  cartItemId?: string
  isLastItems?: boolean
}

export const SummaryVariantCard = ({ cartItemId }: VariantCardProps) => {
  const { cart, quantity, removeLineItem } = useShopify()
  const intl = useIntl()
  const productData = useMemo(() => {
    let variant = cart?.lines?.edges.find(({ node }) => node?.id == cartItemId)

    return variant ? (variant?.node?.merchandise?.product as Product) : null
  }, [quantity, cart])

  const cartProduct = useMemo(() => {
    const variant = cart?.lines?.edges.find(({ node }) => {
      return node?.id == cartItemId ? true : false
    })

    return variant?.node as CartLine
  }, [quantity, cart])

  const totalVariantPrice = useMemo(() => {
    const totalPrice = cartProduct
      ? cartProduct.quantity *
        Number(
          (
            Number(cartProduct?.merchandise?.price?.amount) *
            shopifyDiscountPriceFactor
          ).toFixed(2),
        )
      : 0

    return {
      amount: totalPrice.toFixed(2),
      currencyCode: cartProduct
        ? cartProduct?.merchandise?.price?.currencyCode
        : null,
    }
  }, [cartProduct])

  const QuantityInputVariantsContainer = () => {
    return (
      <Flex width="100%" justifyContent={'center'}>
        <Box width="auto" position="relative">
          <QuantityInputVariants
            cartProduct={cartProduct}
            variant={cartProduct?.merchandise as ProductVariant}
            customPL={{ all: 1, md: 0 }}
            small
          />
        </Box>
      </Flex>
    )
  }

  return (
    <Flex
      position={'relative'}
      width="100%"
      p={1.25}
      mt={1.25}
      alignItems={'center'}
      borderTop={'1px solid rgba(0, 0, 0, 0.2)'}
    >
      <Flex width={'auto'}>
        <Box
          position="relative"
          flexShrink={0}
          mr={[1]}
          width={[84]}
          height={[84]}
        >
          <Image
            height="100%"
            width="100%"
            layout="fill"
            objectFit="contain"
            quality={30}
            src={productData?.images?.edges?.[0]?.node?.url}
          />
        </Box>
      </Flex>

      <Flex width="100%" alignItems={'center'} gap={{ all: '0', md: 2 }}>
        <Box width="100%" display={{ all: 'none', md: 'flex' }}>
          <Flex flexDirection="column" width="100%" height="100%">
            <Text fontWeight={'900'}>
              <div>{productData?.title}</div>
              <div>
                {cartProduct?.merchandise?.selectedOptions[0].name}:{' '}
                {cartProduct?.merchandise?.selectedOptions[0].value}
              </div>
            </Text>
            {/* {!!productData?.description && (
              <Text mt={[0.5]} lineHeight="1" fontSize={'14px'}>
                {productData?.description}
              </Text>
            )} */}
          </Flex>
        </Box>

        <Flex
          display="flex"
          alignItems={['center']}
          flexDirection={['row', 'row', 'column']}
          width="100%"
          height="100%"
        >
          <Box display={{ all: 'flex', md: 'none' }} width="100%">
            <Flex flexDirection="column" width="100%" height="100%">
              <Text fontWeight={'900'}>{productData?.title}</Text>
              <Text fontSize={1.5} fontWeight={'900'}>
                {cartProduct?.merchandise?.selectedOptions[0].name}:{' '}
                {cartProduct?.merchandise?.selectedOptions[0].value}
              </Text>
              <Button
                display={['block', 'block', 'none']}
                mt={1}
                variant="unstyled"
                color="grey500"
                onClick={() => {
                  const portion =
                    cartProduct?.merchandise?.selectedOptions.find(
                      el => el.name === 'Portion',
                    )
                  const gaData = {
                    currency: cartProduct?.merchandise?.price?.currencyCode,
                    amount:
                      Number(cartProduct?.merchandise?.price?.amount) *
                      cartProduct?.quantity,
                    items: [
                      {
                        item_id: cartProduct?.merchandise?.sku,
                        item_name: cartProduct?.merchandise?.product?.title,
                        item_brand: cartProduct?.merchandise?.product?.vendor,
                        item_variant: portion?.value,
                        price: Number(cartProduct?.merchandise?.price?.amount),
                        quantity: cartProduct?.quantity,
                      },
                    ],
                  }

                  gaRemoveFromCartEvent(gaData)
                  removeLineItem(cartProduct?.id)
                }}
                transition="0.2s color ease-out"
                fontSize={1.5}
                css={css({
                  textTransform: 'uppercase',
                  '&:hover, &:focus': {
                    color: 'grey900',
                  },
                })}
              >
                {intl.formatMessage(messages.remove)}
              </Button>
              {/* {!!productData?.description && (
                <Text mt={[0.5]} lineHeight="1" fontSize={'14px'}>
                  {productData?.description}
                </Text>
              )} */}
            </Flex>
          </Box>
          <Flex
            display="flex"
            width="100%"
            height="100%"
            alignItems={'center'}
            justifyContent={['flex-end', 'flex-end', 'center']}
            mt={{ all: 1.5, md: 0 }}
          >
            <Box display={{ all: 'block', md: 'none' }}>
              {/* <QuantityInputVariantsContainer /> */}
            </Box>

            <Button
              display={['none', 'none', 'block']}
              variant="unstyled"
              color="grey500"
              onClick={() => {
                const portion = cartProduct?.merchandise?.selectedOptions.find(
                  el => el.name === 'Portion',
                )
                const gaData = {
                  currency: cartProduct?.merchandise?.price?.currencyCode,
                  amount:
                    Number(cartProduct?.merchandise?.price?.amount) *
                    cartProduct?.quantity,
                  items: [
                    {
                      item_id: cartProduct?.merchandise?.sku,
                      item_name: cartProduct?.merchandise?.product?.title,
                      item_brand: cartProduct?.merchandise?.product?.vendor,
                      item_variant: portion?.value,
                      price: Number(cartProduct?.merchandise?.price?.amount),
                      quantity: cartProduct?.quantity,
                    },
                  ],
                }

                gaRemoveFromCartEvent(gaData)
                removeLineItem(cartProduct?.id)
              }}
              transition="0.2s color ease-out"
              fontSize={2}
              css={css({
                textTransform: 'uppercase',
                '&:hover, &:focus': {
                  color: 'grey900',
                },
              })}
            >
              {intl.formatMessage(messages.remove)}
            </Button>
          </Flex>
        </Flex>

        <Box width={'100%'} display={{ all: 'none', md: 'block' }}>
          <QuantityInputVariantsContainer />
        </Box>
        <Box width={'100%'} display={{ all: 'none', md: 'block' }}>
          <Flex mr={4} justifyContent={'end'}>
            <Text fontSize={2.5}>{formatPrice(totalVariantPrice)}</Text>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  )
}
